import { RouterSelectors } from '@next/core-lib/router-store';
import { createSelector, createFeatureSelector } from '@ngrx/store';

import Utils from 'src/app/shared/utils';
import { AnimalGroupVeterinarians } from 'src/assets/data/AnimalGroupVeterinarians';

import { CycleModuleState } from '../../models/cycle.model';
import { adapter } from '../reducers/cycle.reducer';

const { selectAll } = adapter.getSelectors();

export const getCycleModuleState = createFeatureSelector<CycleModuleState>('cycleModule');

export const getCyclesState = createSelector(getCycleModuleState, (state) => state.cycles);

export const getSelectedCycleId = createSelector(
  getCycleModuleState,
  RouterSelectors.getRouteParam('cycleId'),
  (state, cycleId) => {
    const routeCycleId = parseInt(cycleId, 10);
    if (!isNaN(routeCycleId) && routeCycleId > 0) {
      return routeCycleId;
    } else {
      return state.selectedCycleId;
    }
  },
);

export const getCycles = createSelector(getCyclesState, selectAll);

export const getSelectedCycle = createSelector(getSelectedCycleId, getCycles, (cycleId, cycles) =>
  cycles.find(({ id }) => id === cycleId),
);

export const getSelectedCycleById = (cycleId: number) =>
  createSelector(getCycles, (cycles) => cycles.find(({ id }) => id === cycleId));

export const getNextCycleNumber = createSelector(getCycles, (cycles) =>
  !!cycles && cycles.length > 0 ? Math.max(...cycles.map((c) => c.cycleNumber)) + 1 : 1,
);

export const getPreviousCycle = createSelector(
  getCycles,
  (cycles) =>
    !!cycles &&
    cycles.length > 0 &&
    cycles.reduce((prev, curr) => (prev.cycleNumber > curr.cycleNumber ? prev : curr)),
);

export const getConcepts = createSelector(getCycleModuleState, (state) => state.concepts);

export const getHatcheries = createSelector(getCycleModuleState, (state) =>
  !!state && !!state.hatcheries
    ? state.hatcheries.slice().sort((a, b) => a.name.localeCompare(b.name))
    : [],
);

export const getSlaughterhouses = createSelector(getCycleModuleState, (state) =>
  !!state && !!state.slaughterhouses
    ? state.slaughterhouses.slice().sort((a, b) => a.name.localeCompare(b.name))
    : [],
);

export const isCycleNumberAllowed = createSelector(
  getCycles,
  getSelectedCycle,
  (cycles, cycle) => (cycleNumber: number) =>
    (cycle && cycle.cycleNumber === cycleNumber && cycle.id) ||
    cycles.findIndex((c) => c.cycleNumber === cycleNumber) === -1,
);

export const isCycleDetailedOverview = createSelector(
  getCycleModuleState,
  (state) => state.isDetailedOverview,
);

export const getSlaughterReports = createSelector(
  getCycleModuleState,
  (state) => state.slaughterReports,
);

export const getLinkedSlaughterInvoices = createSelector(
  getCycleModuleState,
  (state) => state.slaughterInvoices.linked,
);

export const getUnlinkedSlaughterInvoices = createSelector(
  getCycleModuleState,
  (state) => state.slaughterInvoices.unlinked,
);

export const getVeterianrians = createSelector(getCycleModuleState, () =>
  Utils.sortAscending(AnimalGroupVeterinarians, 'name'),
);

export const getFarmResources = createSelector(getCycleModuleState, (state) => state.farmResources);

export const getCycleResources = createSelector(
  getCycleModuleState,
  (state) => state.cycleResources,
);

export const getCarbonFootprint = createSelector(
  getCycleModuleState,
  (state) => state.carbonFootprint,
);

export const selectSustainabilityNoticeVisibility = createSelector(
  getCycleModuleState,
  (state) => state.sustainabilityNoticeVisible,
);
